import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  API_STATUS,
  ApiStatus,
  HISTORY_SAVE_LIMIT_DATE,
  PAGE_ROUTE_PATH,
  TRANS_MODE,
  URL_PARAMS_KEY,
} from '@/constants';
import { SESSION_STORAGE_KEY } from '@/constants/ga';
import { isHomeStateType } from '@/constants/home';
import {
  TOKEN_AUTH_API_RESULT_CODE,
  useTokenAuthApi,
  usePlanInfoApi,
} from '@/features/api';
import { useNeedAgreement, usePtidExpired } from '@/features/expired';
import { useUrlParameterLang } from '@/features/langupdate';
import { useLogoutExpiredSerial } from '@/features/logout';
import { useAuthSerial } from '@/hooks/useAuthSerial';
import { useBrowserHistory } from '@/hooks/useBrowserHistory';
import { useLoginUserInfo } from '@/hooks/useLoginUserInfo';
import { useUrlParameter } from '@/hooks/useUrlParameter';
import { clearAdParams, sanitizeAdParams } from '@/utils/ad';
import { sendFirstLoginConversionEvent } from '@/utils/ga';

/**
 * 本カスタムフックからの返却値
 */
export type UseHomeValue = {
  // トークンチェックAPI呼び出し状況
  tokenAuthStatus: ApiStatus;
  // [マイク翻訳]ボタンがクリックされた場合の処理
  clickMicButton: () => void;
  // [スピーカー翻訳]ボタンがクリックされた場合の処理
  clickSpeakerButton: () => void;
  // ライセンス期限チェック&ログアウト処理状況
  expiredSerialLogoutState: ApiStatus;
  // 再ログインが必要か否か(true=必要)
  needsLoginAgain: boolean;
  // シリアル連携ダイアログを開くか否か
  isOpenSerialLinkDialog: boolean;
  // シリアル連携ダイアログを閉じる処理
  onClickSerialLinkDialogClose: () => void;
  // シリアル連携処理成功時に実行させたい処理
  handleSerialLinkSuccess: () => void;
  // プラン情報取得API呼び出し状況
  planInfoState: ApiStatus;
};

/**
 * ホーム画面（モード選択画面） hooks
 */
export const useHome = (): UseHomeValue => {
  const { tokenAuthState, fetchTokenAuth } = useTokenAuthApi();
  const navigate = useNavigate();
  const { ptidExpired } = usePtidExpired();
  const { saveURLParamToLocalStorage } = useUrlParameterLang();
  const { needAgreement } = useNeedAgreement();
  const { deleteHistoryOverLimit } = useBrowserHistory();
  const { logoutIfExpiredSerial, expiredSerialLogoutState } =
    useLogoutExpiredSerial();
  const { state } = useLocation();
  const { isSerialPlan, isFreePlan, setPlanInfoFromApi, isPlanInfoFetched } =
    useLoginUserInfo();
  const { findParams } = useUrlParameter();
  const { setSerial } = useAuthSerial();
  const { planInfoState, fetchPlanInfo } = usePlanInfoApi();

  /**
   * シリアル関連操作を実行可能かどうかのフラグ
   * トークンチェック有効 かつ プラン情報取得済み の場合に true
   */
  const canExecuteSerialOperations = useMemo(
    () => tokenAuthState.status === API_STATUS.SUCCESS && isPlanInfoFetched,
    [tokenAuthState, isPlanInfoFetched],
  );

  /**
   * [マイク翻訳]ボタンがクリックされた場合の処理
   */
  const clickMicButton = useCallback(() => {
    navigate(PAGE_ROUTE_PATH.TRANSLATION.MIC, { state: TRANS_MODE.MIC });
  }, [navigate]);

  /**
   * [スピーカー翻訳]ボタンがクリックされた場合の処理
   */
  const clickSpeakerButton = useCallback(() => {
    navigate(PAGE_ROUTE_PATH.TRANSLATION.SPEAKER, {
      state: TRANS_MODE.SPEAKER,
    });
  }, [navigate]);

  /**
   * シリアルで利用中の場合のみ、シリアルの期限をチェックして期限切れだった場合に以下の処理実行
   * ・自動ログアウト
   * ・コード付きでシリアル画面に遷移
   */
  const navigateSerialViewAfterLogoutIfSerialExpired = useCallback(
    (serialCode: string) => {
      if (!isSerialPlan) {
        return;
      }

      logoutIfExpiredSerial(serialCode);
    },
    [isSerialPlan, logoutIfExpiredSerial],
  );

  /**
   * 再ログインが必要か否か(true=必要)
   */
  const [needsLoginAgain, setNeedsLoginAgain] = useState<boolean>(false);

  /**
   * シリアル連携ダイアログを開くか否か
   */
  const [isOpenSerialLinkDialog, setIsOpenSerialLinkDialog] =
    useState<boolean>(false);

  /**
   * お試しプランで利用中でかつ、
   * URLパラメータにサインアップ連携用のシリアルコードが指定されている場合は取得して保存
   */
  const saveSignupSerialCode = useCallback(() => {
    if (!isFreePlan) {
      return;
    }
    const serialCode = findParams(URL_PARAMS_KEY.HOME.signupCode);
    if (!serialCode) {
      return;
    }
    setSerial(serialCode);
    setIsOpenSerialLinkDialog(true);
  }, [findParams, isFreePlan, setSerial]);

  /**
   * 状態：画面表示時
   */
  useEffect(() => {
    // URLパラメータに指定されたUI言語をローカルストレージに保存
    saveURLParamToLocalStorage();

    // トークンの期限チェック
    fetchTokenAuth();

    // ローカルストレージから保存期間が過ぎた履歴を削除
    deleteHistoryOverLimit(HISTORY_SAVE_LIMIT_DATE);

    // ── 初回ログイン時のコンバージョン計測（マウント時1回のみ実行） ──
    // バックエンドが IDトークンの ad_source クレームを URL パラメータとして付与する。
    // /home?lang=ja&signup_code=xxx&ad_source={"utm_source":"google",...}
    const adSourceParam = findParams(URL_PARAMS_KEY.HOME.adSource);
    if (
      adSourceParam &&
      !sessionStorage.getItem(SESSION_STORAGE_KEY.FIRST_LOGIN_FIRED)
    ) {
      try {
        const adParams = sanitizeAdParams(JSON.parse(adSourceParam));
        sendFirstLoginConversionEvent(adParams);
        sessionStorage.setItem(SESSION_STORAGE_KEY.FIRST_LOGIN_FIRED, '1');
        clearAdParams();
      } catch {
        // パースエラー時はコンバージョン計測をスキップ
      } finally {
        // URLから ad_source を除去し、リファラ漏洩・URL共有による誤発火・履歴汚染を防止
        try {
          const url = new URL(window.location.href);
          if (url.searchParams.has(URL_PARAMS_KEY.HOME.adSource)) {
            url.searchParams.delete(URL_PARAMS_KEY.HOME.adSource);
            window.history.replaceState(null, '', url.toString());
          }
        } catch {
          // replaceState 失敗時は無視
        }
      }
    }

    // コンポーネントのマウント時に一度だけ実行
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * トークンチェックAPIからの返却値を監視
   */
  useEffect(() => {
    if (
      tokenAuthState.status === API_STATUS.FAILED &&
      tokenAuthState.data?.resultCode ===
        TOKEN_AUTH_API_RESULT_CODE.INFO_EXPIRED_IDTOKEN
    ) {
      // PTID期限切れ処理実行
      ptidExpired();

      return;
    }

    // INFO_NEED_AGREEMENTの場合、利用規約画面に遷移
    if (
      tokenAuthState.data?.resultCode ===
      TOKEN_AUTH_API_RESULT_CODE.INFO_NEED_AGREEMENT
    ) {
      needAgreement();
    }
  }, [needAgreement, ptidExpired, tokenAuthState]);

  /**
   * プラン情報取得
   */
  useEffect(() => {
    if (tokenAuthState.status !== API_STATUS.SUCCESS) {
      return;
    }
    if (planInfoState.status !== API_STATUS.IDLE) {
      return;
    }

    if (isPlanInfoFetched) {
      return; // 既にプラン情報を取得済みの場合は何もしない
    }

    // プラン情報取得（プライベートAPI使用）
    fetchPlanInfo({ urlKey: '' }, true);
  }, [
    fetchPlanInfo,
    isPlanInfoFetched,
    planInfoState.status,
    tokenAuthState.status,
  ]);

  /**
   * プラン情報の取得に成功した場合の処理
   */
  useEffect(() => {
    if (planInfoState.status !== API_STATUS.SUCCESS) {
      return;
    }

    setPlanInfoFromApi(planInfoState.data?.planInfo);
  }, [planInfoState, setPlanInfoFromApi]);

  /**
   * シリアル関連操作の実行
   */
  useEffect(() => {
    if (!canExecuteSerialOperations) {
      return;
    }

    // stateにシリアル番号が指定された場合のみ、シリアルの期限チェックを行う
    if (isHomeStateType(state) && state.serialCode) {
      navigateSerialViewAfterLogoutIfSerialExpired(state.serialCode);
    }

    // URLパラメータにサインアップ連携用のシリアルコードが指定されている場合は取得して保存
    saveSignupSerialCode();
  }, [
    canExecuteSerialOperations,
    navigateSerialViewAfterLogoutIfSerialExpired,
    saveSignupSerialCode,
    state,
  ]);

  /**
   * シリアル連携ダイアログを閉じる処理
   */
  const onClickSerialLinkDialogClose = useCallback(() => {
    setIsOpenSerialLinkDialog(false);
  }, []);

  /**
   * シリアル連携処理成功時に実行させたい処理
   */
  const handleSerialLinkSuccess = useCallback(() => {
    setIsOpenSerialLinkDialog(false);
    setNeedsLoginAgain(true);
  }, []);

  return {
    tokenAuthStatus: tokenAuthState.status,
    clickMicButton,
    clickSpeakerButton,
    expiredSerialLogoutState: expiredSerialLogoutState.status,
    needsLoginAgain,
    isOpenSerialLinkDialog,
    onClickSerialLinkDialogClose,
    handleSerialLinkSuccess,
    planInfoState: planInfoState.status,
  };
};
